import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../utils/tokenStorage';
import './StaticPages.css';

const STATES = ['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'];

const CATEGORIES = [
  { key: 'healthcare', label: 'Healthcare Access', icon: '🏥', desc: 'Hospitals & pharmacies within 3km' },
  { key: 'transport', label: 'Public Transport', icon: '🚌', desc: 'Bus stops & train stations within 3km' },
  { key: 'shopping', label: 'Shopping', icon: '🛒', desc: 'Supermarkets within 3km' },
  { key: 'dining', label: 'Dining & Cafes', icon: '☕', desc: 'Cafes & restaurants within 3km' },
  { key: 'parks', label: 'Parks & Green Space', icon: '🌳', desc: 'Parks within 3km' },
  { key: 'walkability', label: 'Walkability', icon: '🚶', desc: 'Density of nearby shops, cafes, parks & transport within an easy 800m walk' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BestLocationsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState('QLD');
  const [priorities, setPriorities] = useState({ healthcare: 3, transport: 3, shopping: 3, dining: 2, parks: 2, walkability: 2 });
  const [minScore, setMinScore] = useState(5);
  const [results, setResults] = useState([]);
  const [searchedState, setSearchedState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [savedSuburbs, setSavedSuburbs] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const isLoggedIn = !!getToken();

  const authHeaders = () => ({ withCredentials: true, headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchSavedSuburbs = async () => {
    if (!isLoggedIn) return;
    try {
      const { data } = await axios.get(`${API}/favorites`, { ...authHeaders(), params: { item_type: 'suburb' } });
      setSavedSuburbs(data);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { fetchSavedSuburbs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isSuburbSaved = (suburbState, city) => savedSuburbs.some(f => f.state === suburbState && f.city === city);

  const toggleSaveSuburb = async (suburbState, city, postcode) => {
    const itemId = `${suburbState}|${city}`;
    setSavingKey(itemId);
    try {
      const existing = savedSuburbs.find(f => f.item_id === itemId);
      if (existing) {
        await axios.delete(`${API}/favorites/${existing.favorite_id}`, authHeaders());
      } else {
        await axios.post(`${API}/favorites`, { item_id: itemId, item_type: 'suburb', city, state: suburbState, postcode }, authHeaders());
      }
      await fetchSavedSuburbs();
    } catch (e) {
      alert('Could not update saved suburbs: ' + (e.response?.data?.detail || e.message));
    }
    setSavingKey(null);
  };

  const handleSearch = async () => {
    setLoading(true); setError(''); setSearched(true);
    const stateAtSearchTime = state;
    try {
      const { data } = await axios.get(`${API}/best-locations`, {
        params: { state: stateAtSearchTime, min_score: minScore, ...priorities },
        timeout: 40000,
      });
      if (data.error && (!data.suburbs || data.suburbs.length === 0)) {
        setError(data.error);
      }
      setResults(data.suburbs || []);
      setSearchedState(stateAtSearchTime);
    } catch (e) {
      setError(e.code === 'ECONNABORTED'
        ? "This is taking longer than expected. Our location data provider may be slow right now — please try again shortly."
        : 'Search failed. Please try again.');
    }
    setLoading(false);
  };

  const scoreColor = (s) => s >= 7 ? '#059669' : s >= 5 ? '#d97706' : '#dc2626';

  return (
    <div className="static-page" data-testid="best-locations-page"><header className="static-header">
        <div className="container">
          <div className="header-content">
            <img src="/logo.png" alt="HSS" className="logo-image" onClick={() => navigate('/')} />
            <nav>
              <button className="btn btn-secondary" onClick={() => navigate('/properties')}>Browse Properties</button>
              <button className="btn btn-primary" onClick={() => navigate('/apply')}>Apply Now</button>
            </nav>
          </div>
        </div>
      </header>

      <section className="page-hero" style={{ background: '#1a2332', padding: '28px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ color: 'white', marginBottom: 6 }}>Best Locations for Seniors</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>Find suburbs scored on what matters most — healthcare, transport, shops, dining, parks &amp; walkability</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="best-locations-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 24 }}>

            {/* Controls */}
            <div style={{ background: '#f8fafb', borderRadius: 12, padding: '20px', border: '1px solid #e5e7eb', alignSelf: 'start' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#1a2332' }}>Search Settings</h3>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>State</label>
                <select value={state} onChange={e => setState(e.target.value)} className="filter-input" data-testid="best-locations-state-select">
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Minimum Overall Score: <strong>{minScore}/10</strong></label>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>Only show suburbs that score at least this overall</div>
                <input type="range" min={1} max={9} value={minScore} onChange={e => setMinScore(parseInt(e.target.value))} style={{ width: '100%' }} data-testid="best-locations-min-score-slider" />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Category Priorities (1–5)</label>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10, lineHeight: 1.4 }}>
                  How important is each category to you?<br/>
                  <strong style={{ color: '#6b7280' }}>1 = not important &nbsp;·&nbsp; 5 = very important</strong><br/>
                  Higher priority categories have more weight in the final score.
                </div>
                {CATEGORIES.map(c => (
                  <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#4b5563' }}>{c.icon} {c.label}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1,2,3,4,5].map(v => (
                        <button key={v} onClick={() => setPriorities(p => ({...p, [c.key]: v}))}
                          title={v === 1 ? 'Not important' : v === 2 ? 'Slightly important' : v === 3 ? 'Moderately important' : v === 4 ? 'Very important' : 'Most important'}
                          data-testid={`priority-${c.key}-${v}`}
                          style={{ width: 24, height: 24, borderRadius: 4, border: '1px solid', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            borderColor: priorities[c.key] >= v ? '#1a2332' : '#d1d5db',
                            background: priorities[c.key] >= v ? '#1a2332' : 'white',
                            color: priorities[c.key] >= v ? 'white' : '#9ca3af' }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={handleSearch} disabled={loading} className="btn btn-primary"
                style={{ width: '100%' }} data-testid="search-locations-btn">
                {loading ? 'Searching...' : 'Find Best Locations'}
              </button>

              {isLoggedIn && savedSuburbs.length > 0 && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }} data-testid="saved-suburbs-panel">
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>★ Your Saved Suburbs ({savedSuburbs.length})</h4>
                  {savedSuburbs.map(f => (
                    <div key={f.favorite_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#4b5563', marginBottom: 6 }} data-testid={`saved-suburb-${f.favorite_id}`}>
                      <span>{f.city}, {f.state}</span>
                      <button onClick={() => toggleSaveSuburb(f.state, f.city, f.postcode)} disabled={savingKey === f.item_id}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}
                        data-testid={`remove-saved-suburb-${f.favorite_id}`}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Results */}
            <div>
              {!searched && (
                <div>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: 8, fontSize: 14 }}>How scores work</div>
                    <p style={{ fontSize: 13, color: '#374151', margin: '0 0 10px', lineHeight: 1.5 }}>
                      Each suburb is scored <strong>0–10 per category</strong> based on how many relevant places are nearby, using live location data.
                      The <strong>Overall Score</strong> is a weighted average — categories you set to priority 5 count more than priority 1.
                    </p>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <tbody>
                        {CATEGORIES.map(c => (
                          <tr key={c.key} style={{ borderBottom: '1px solid #e0f2fe' }}>
                            <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{c.icon} <strong>{c.label}</strong></td>
                            <td style={{ padding: '5px 6px', color: '#6b7280' }}>{c.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📍</div>
                    <p>Set your priorities and click <strong>Find Best Locations</strong></p>
                  </div>
                </div>
              )}

              {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#b91c1c', marginBottom: 16 }} data-testid="best-locations-error">{error}</div>}

              {loading && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                  <p>Scoring suburbs using live location data… the first search for a state can take up to 30 seconds while we gather fresh data; later searches are much faster.</p>
                </div>
              )}

              {!loading && searched && results.length === 0 && !error && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <p>No suburbs found matching your criteria. Try lowering the minimum score.</p>
                </div>
              )}

              {results.length > 0 && (
                <div data-testid="best-locations-results">
                  <div style={{ marginBottom: 12, color: '#6b7280', fontSize: 13 }}>{results.length} suburbs found in {searchedState}</div>
                  {results.map((suburb, i) => (
                    <div key={`${suburb.name}-${suburb.postcode}`} data-testid={`suburb-result-${i}`} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px', marginBottom: 10,
                      borderLeft: `4px solid ${scoreColor(suburb.overall_score)}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 16, color: '#1a2332' }}>#{i+1} {suburb.name}, {searchedState}</div>
                          {suburb.postcode && <div style={{ fontSize: 12, color: '#9ca3af' }}>{suburb.postcode}</div>}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 28, fontWeight: 900, color: scoreColor(suburb.overall_score) }}>{suburb.overall_score.toFixed(1)}</div>
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>/ 10</div>
                          {isLoggedIn && (
                            <button onClick={() => toggleSaveSuburb(searchedState, suburb.name, suburb.postcode)}
                              disabled={savingKey === `${searchedState}|${suburb.name}`}
                              title={isSuburbSaved(searchedState, suburb.name) ? 'Remove from saved suburbs' : 'Save this suburb'}
                              data-testid={`save-suburb-btn-${i}`}
                              style={{ marginTop: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: isSuburbSaved(searchedState, suburb.name) ? '#d97706' : '#d1d5db' }}>
                              {isSuburbSaved(searchedState, suburb.name) ? '★' : '☆'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 10 }}>
                        {CATEGORIES.map(c => (
                          <div key={c.key} style={{ fontSize: 11, color: '#6b7280', display: 'flex', flexDirection: 'column', background: '#f8fafb', borderRadius: 4, padding: '5px 8px' }}>
                            <span style={{ marginBottom: 2 }}>{c.icon} {c.label}</span>
                            <span style={{ fontWeight: 700, fontSize: 14, color: scoreColor(suburb.scores?.[c.key] || 0) }}>{(suburb.scores?.[c.key] || 0).toFixed(1)}<span style={{ fontSize: 10, fontWeight: 400, color: '#9ca3af' }}>/10</span></span>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => navigate(`/properties?state=${encodeURIComponent(searchedState)}&city=${encodeURIComponent(suburb.name)}`)}
                        data-testid={`view-properties-btn-${i}`}
                        style={{ marginTop: 10, fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        View properties in {suburb.name} →
                      </button>
                    </div>
                  ))}
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 16, textAlign: 'center' }}>
                    © OpenStreetMap contributors · Powered by Geoapify
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="static-footer">
        <div className="container">
          <p>&copy; 2026 House Sharing Seniors. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
